/**
 * WatchOutsEditor (360-B, approved on the idea-session canvas 2026-09-08) —
 * the ONE place the callouts are changed. Opened from "Edit" on the rail.
 *
 *   SHOWING ON THE RECORD   every row the record shows: automatic ones
 *                           (plan expiring, Do Not Service — shown, not
 *                           editable, they clear themselves) and the stored
 *                           ones, each with its kind / scope chips, who added
 *                           it, and a switch to hide it without deleting.
 *   ADD A WATCH-OUT         pick the kind, type it the way you'd say it on
 *                           the phone, add. Born confirmed, stamped with who.
 *   PEGASUS FOUND THESE     the extractor's reads of old notes — nothing here
 *                           shows to the team until someone keeps it.
 *
 * Every write lands in extracted_signal (authenticated RLS + zzaudit), so the
 * customer page, the dispatch board and the tech app read the same list.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, X, Sparkles, Pencil, Plus, RotateCcw, Ban, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/cn";
import { SlideOver } from "@/components/ui/SlideOver";
import { onErrToast } from "@/components/ui/mutationToast";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useResolveSignal, useUpdateSignalValue, useAddSignal, useSetDoNotService, useCustomerSignals, type CustomerSignal } from "./api";
import { useActiveTechs } from "@/features/board/api";
import { techFirst, setTechRequest, currentTechRequest } from "./techRequest";
import {
  useWatchouts, useSetSignalHidden, useRefileSignal, sentenceFor, kindOf, provenance,
  ADD_KINDS, WATCH_KINDS, SEVERITY_ORDER, type WatchSeverity, type WatchoutsArgs, type WatchItem,
} from "./watchouts";

const DOT: Record<WatchSeverity, string> = { blocking: "bg-rose-500", routing: "bg-amber-500", access: "bg-sky-500", info: "bg-slate-300" };
const CHIP_TONE: Record<string, string> = {
  Caution: "bg-rose-50 text-rose-800 ring-rose-200",
  Routing: "bg-amber-50 text-amber-900 ring-amber-200",
  Access: "bg-sky-50 text-sky-900 ring-sky-200",
  Note: "bg-slate-100 text-slate-700 ring-slate-200",
  Automatic: "bg-slate-100 text-slate-600 ring-slate-200",
};

function Eyebrow({ children, count, hint }: { children: React.ReactNode; count?: number; hint?: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">{children}</span>
      {count !== undefined && count > 0 && <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-bold tabular-nums text-slate-600">{count}</span>}
      {hint && <span className="ml-auto text-xs text-slate-400">{hint}</span>}
    </div>
  );
}

function Toggle({ on, onChange, title }: { on: boolean; onChange: (v: boolean) => void; title: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      title={title}
      onClick={() => onChange(!on)}
      className={cn(
        "relative h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        on ? "bg-brand-600" : "bg-slate-300",
      )}
    >
      <span className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all", on ? "left-[18px]" : "left-0.5")} />
    </button>
  );
}

/** A kind or scope chip that is really a <select> — click to re-file. */
function ChipSelect({ value, options, onChange, tone, disabled, title }: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  tone: string;
  disabled?: boolean;
  title?: string;
}) {
  const current = options.find((o) => o.value === value)?.label ?? value;
  return (
    <span className={cn("relative inline-flex h-6 items-center rounded-full pl-2.5 pr-6 text-[11px] font-semibold ring-1 ring-inset", tone, disabled && "pr-2.5 opacity-80")} title={title}>
      {current}
      {!disabled && <ChevronDown className="pointer-events-none absolute right-1.5 h-3 w-3 opacity-60" />}
      {!disabled && (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={title}
          className="absolute inset-0 cursor-pointer opacity-0"
        >
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </span>
  );
}

const KIND_OPTIONS = Object.entries(WATCH_KINDS).map(([value, k]) => ({ value, label: `${k.family} · ${k.label}` }));

function StoredRow({ s, locno, actions }: { s: CustomerSignal; locno?: string | null; actions: React.ReactNode }) {
  const sen = sentenceFor(s);
  const k = kindOf(s.signal_type);
  const refile = useRefileSignal();
  const update = useUpdateSignalValue();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(s.value);
  const scopeOptions = [{ value: "all", label: "All Properties" }, ...(locno || s.locno ? [{ value: "this", label: "This Property" }] : [])];
  return (
    <li className="flex items-start gap-3 py-2.5">
      <span className={cn("mt-2 h-2 w-2 shrink-0 rounded-full", DOT[sen.severity])} />
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              rows={2}
              className="w-full resize-none rounded-md border border-hairline bg-white px-2 py-1.5 text-sm leading-snug focus:border-brand-500 focus:outline-none"
            />
            <div className="mt-1.5 flex items-center gap-2">
              <button
                type="button"
                disabled={!draft.trim() || update.isPending}
                onClick={() => update.mutate({ id: s.id, value: draft, custno: s.custno }, { onSuccess: () => setEditing(false), onError: onErrToast("Couldn't save the wording") })}
                className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
              >
                Save
              </button>
              <button type="button" onClick={() => { setEditing(false); setDraft(s.value); }} className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700">Cancel</button>
              {s.source_text && <span className="ml-auto truncate text-[11px] italic text-slate-400" title={s.source_text}>from: “{s.source_text.slice(0, 50)}{s.source_text.length > 50 ? "…" : ""}”</span>}
            </div>
          </div>
        ) : (
          <div className="group/row flex items-start gap-1.5">
            <span className="min-w-0 text-[13px] leading-snug text-slate-900">{sen.text}</span>
            <button type="button" onClick={() => { setDraft(s.value); setEditing(true); }} title="Change the wording" className="mt-px shrink-0 rounded p-0.5 text-slate-300 opacity-0 transition-opacity hover:text-slate-600 group-hover/row:opacity-100 focus-visible:opacity-100">
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <ChipSelect
            value={s.signal_type}
            options={KIND_OPTIONS}
            tone={CHIP_TONE[k.family]}
            title="What kind of watch-out this is — click to re-file"
            onChange={(v) => refile.mutate({ id: s.id, signal_type: v }, { onError: onErrToast("Couldn't re-file it — the same note may already be filed that way") })}
          />
          <ChipSelect
            value={s.locno ? "this" : "all"}
            options={scopeOptions}
            tone="bg-white text-slate-600 ring-slate-200"
            title="Which property this applies to"
            onChange={(v) => refile.mutate({ id: s.id, locno: v === "this" ? (s.locno ?? locno ?? null) : null }, { onError: onErrToast("Couldn't change where it applies") })}
          />
          <span className="text-[11px] text-slate-400">{provenance(s)}</span>
        </div>
      </div>
      {actions}
    </li>
  );
}

function AutoRow({ it }: { it: WatchItem }) {
  return (
    <li className="flex items-start gap-3 py-2.5 opacity-90">
      <span className={cn("mt-2 h-2 w-2 shrink-0 rounded-full", DOT[it.severity])} />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] leading-snug text-slate-900">{it.text}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className={cn("inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-semibold ring-1 ring-inset", CHIP_TONE.Automatic)}>
            Automatic · {it.kind === "renewal" || it.kind === "lapsed" ? "membership" : "account"}
          </span>
          <span className="text-[11px] text-slate-400">{it.auto}</span>
        </div>
      </div>
    </li>
  );
}

export function WatchOutsEditor({ open, onClose, customerName, ...args }: WatchoutsArgs & { open: boolean; onClose: () => void; customerName: string }) {
  const footer = (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-slate-400">The customer page, the dispatch board and the tech app read this list.</span>
      <button type="button" onClick={onClose} className="rounded-md border border-hairline bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-800 shadow-e1 hover:bg-slate-50">Done</button>
    </div>
  );
  return (
    <SlideOver
      open={open}
      onClose={onClose}
      size="lg"
      title={`Watch-outs · ${customerName}`}
      subtitle="What every dispatcher and tech sees before touching this account"
      footer={footer}
    >
      <WatchOutsEditorBody {...args} />
    </SlideOver>
  );
}

/**
 * EDIT-SUITE-01 (34i97): the editor's body on its own, so the Edit Customer
 * suite embeds the SAME editor (one editor, many doors) — nothing is duplicated.
 */
export function WatchOutsEditorBody(args: WatchoutsArgs) {
  const { custno, locno } = args;
  const w = useWatchouts(args);
  const resolve = useResolveSignal();
  const hide = useSetSignalHidden();
  const add = useAddSignal();
  const setDns = useSetDoNotService();
  const canDns = useCan("customers.mark_inactive");

  const [kind, setKind] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [thisPropertyOnly, setThisPropertyOnly] = useState(!!locno);
  const [dnsOpen, setDnsOpen] = useState(false);
  const [dnsReason, setDnsReason] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  // TECH-REQUEST-PICKER (Seth 2026-10-22): for "Don't send / requests a tech"
  // the roster is a row of chips — pick, don't type. A request written from a
  // chip goes through the shared writer (older requests switch off, a
  // switched-off "Jeremy" is revived instead of tripping the dedupe index).
  const qc = useQueryClient();
  const { data: techs } = useActiveTechs("03");
  const { data: signals } = useCustomerSignals(custno);
  const roster = useMemo(() => (techs ?? []).slice().sort((a, b) => techFirst(a).localeCompare(techFirst(b))), [techs]);
  const onFileTech = useMemo(() => currentTechRequest(signals, roster), [signals, roster]);
  const [techMode, setTechMode] = useState<"requests" | "exclude">("requests");
  const [techPicks, setTechPicks] = useState<string[]>([]);
  const [techBusy, setTechBusy] = useState(false);
  const techText = (mode: "requests" | "exclude", picks: string[]) => {
    const names = picks.map((e) => roster.find((t) => t.empno === e)).filter((t): t is NonNullable<typeof t> => !!t).map(techFirst);
    return names.length === 0 ? "" : `${mode === "exclude" ? "Don't send" : "Requests"} ${names.join(" or ")}`;
  };
  const toggleTech = (empno: string) => {
    const next = techPicks.includes(empno) ? techPicks.filter((e) => e !== empno) : [...techPicks, empno];
    setTechPicks(next);
    setText(techText(techMode, next));
  };
  const setMode = (m: "requests" | "exclude") => { setTechMode(m); if (techPicks.length > 0) setText(techText(m, techPicks)); };
  const pickKind = (t: string) => { setKind(t); setDnsOpen(false); if (t !== "tech_pref") { setTechPicks([]); } };

  // Showing = automatic rows + every confirmed, switched-on row (the rail
  // skips "goes by"; the editor lists it so it has a home).
  const showingRows = useMemo(() => {
    const autos = w.items.filter((it) => it.auto);
    const stored = [...w.showing].sort((a, b) => SEVERITY_ORDER[kindOf(a.signal_type).severity] - SEVERITY_ORDER[kindOf(b.signal_type).severity]);
    return { autos, stored };
  }, [w.items, w.showing]);

  const submitAdd = async () => {
    if (!kind || !text.trim() || techBusy) return;
    const scope = thisPropertyOnly && locno ? locno : null;
    const done = () => { setText(""); setKind(null); setTechPicks([]); };
    if (kind === "tech_pref" && techPicks.length > 0 && text.trim() === techText(techMode, techPicks)) {
      const picks = techPicks.map((e) => roster.find((t) => t.empno === e)).filter((t): t is NonNullable<typeof t> => !!t).map((t) => ({ empno: t.empno, first: techFirst(t) }));
      if (techMode === "requests") {
        setTechBusy(true);
        try {
          await setTechRequest({ custno, locno: scope, picks, signals, source: "watch_outs" });
          qc.invalidateQueries({ queryKey: ["customer_signals", custno] });
          done();
        } catch (e) { onErrToast("Couldn't add it")(e); } finally { setTechBusy(false); }
        return;
      }
      add.mutate(
        { custno, locno: scope, signal_type: kind, value: text, detail: { source: "watch_outs", exclude: true, empnos: picks.map((p) => p.empno) } },
        { onSuccess: done, onError: onErrToast("Couldn't add it — the same note may already be on file") },
      );
      return;
    }
    add.mutate(
      { custno, locno: scope, signal_type: kind, value: text },
      { onSuccess: done, onError: onErrToast("Couldn't add it — the same note may already be on file") },
    );
  };

  return (
    <div>
      {/* ---------------------------------------------------------------- showing */}
      <section>
        <Eyebrow count={showingRows.autos.length + showingRows.stored.length} hint="switch off to hide without deleting">Showing on the record</Eyebrow>
        {showingRows.autos.length + showingRows.stored.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">Nothing on the record yet — add one below, or keep something Pegasus found.</p>
        ) : (
          <ul className="mt-1 divide-y divide-slate-100">
            {showingRows.autos.map((it) => <AutoRow key={it.key} it={it} />)}
            {showingRows.stored.map((s) => (
              <StoredRow
                key={s.id}
                s={s}
                locno={locno}
                actions={
                  <div className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
                    <Toggle on onChange={() => hide.mutate({ id: s.id, hidden: true }, { onError: onErrToast("Couldn't switch it off") })} title="Switch off — hides it from the record without deleting" />
                    <RemoveButton onRemove={() => resolve.mutate({ id: s.id, status: "dismissed" }, { onError: onErrToast("Couldn't remove it") })} />
                  </div>
                }
              />
            ))}
          </ul>
        )}
        {w.hidden.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <button type="button" onClick={() => setShowHidden((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
              {showHidden ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {w.hidden.length} switched off
            </button>
            {showHidden && (
              <ul className="divide-y divide-slate-100 opacity-70">
                {w.hidden.map((s) => (
                  <StoredRow
                    key={s.id}
                    s={s}
                    locno={locno}
                    actions={<Toggle on={false} onChange={() => hide.mutate({ id: s.id, hidden: false }, { onError: onErrToast("Couldn't switch it on") })} title="Switch on — shows on the record again" />}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* ---------------------------------------------------------------- add */}
      <section className="mt-5 rounded-xl bg-slate-50 p-3.5">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Plus className="h-4 w-4 text-brand-600" /> Add a watch-out</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {ADD_KINDS.map((t) => {
            const k = WATCH_KINDS[t];
            const Icon = k.icon;
            const on = kind === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => pickKind(t)}
                className={cn(
                  "inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-[11px] font-semibold ring-1 ring-inset transition-colors",
                  on ? "bg-brand-700 text-white ring-brand-700" : CHIP_TONE[k.family] + " hover:brightness-95",
                )}
              >
                <Icon className="h-3 w-3" /> {k.addLabel}
              </button>
            );
          })}
          {canDns && !args.dns && (
            <button
              type="button"
              onClick={() => { setDnsOpen((v) => !v); setKind(null); }}
              className={cn("inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-[11px] font-semibold ring-1 ring-inset transition-colors", dnsOpen ? "bg-rose-700 text-white ring-rose-700" : "bg-rose-50 text-rose-800 ring-rose-200 hover:brightness-95")}
            >
              <Ban className="h-3 w-3" /> Do not service
            </button>
          )}
        </div>
        {kind && (
          <div className="mt-2.5">
            {kind === "tech_pref" && (
              <div className="mb-2 rounded-lg border border-hairline bg-white px-3 py-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {(["requests", "exclude"] as const).map((m) => (
                    <button key={m} type="button" onClick={() => setMode(m)}
                      className={cn("inline-flex h-6 items-center rounded-full px-2 text-[11px] font-semibold ring-1 ring-inset transition-colors",
                        techMode === m ? (m === "exclude" ? "bg-rose-700 text-white ring-rose-700" : "bg-brand-700 text-white ring-brand-700") : "bg-slate-50 text-slate-600 ring-slate-200 hover:bg-slate-100")}>
                      {m === "exclude" ? "Don't send" : "Requests"}
                    </button>
                  ))}
                  <span className="mx-1 h-4 w-px bg-slate-200" />
                  {roster.map((t) => {
                    const on = techPicks.includes(t.empno);
                    return (
                      <button key={t.empno} type="button" onClick={() => toggleTech(t.empno)}
                        className={cn("inline-flex h-6 items-center rounded-full px-2 text-[11px] font-semibold ring-1 ring-inset transition-colors",
                          on ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")}>
                        {techFirst(t)}
                      </button>
                    );
                  })}
                  {roster.length === 0 && <span className="text-[11px] text-slate-400">Loading the roster…</span>}
                </div>
                <div className="mt-1.5 text-[11px] text-slate-400">
                  {techMode === "requests"
                    ? (onFileTech ? `On file: ${onFileTech.techs.map(techFirst).join(" or ") || onFileTech.row.value} · a new request switches it off` : "Pick one — or two if either is fine")
                    : "Stays alongside any request · the board flags a booking onto them"}
                </div>
              </div>
            )}
            <div className="flex items-start gap-2">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void submitAdd(); } }}
                autoFocus
                rows={2}
                placeholder={WATCH_KINDS[kind].placeholder ?? "Type it the way you'd say it on the phone"}
                className="min-w-0 flex-1 resize-none rounded-lg border border-hairline bg-white px-3 py-2 text-sm leading-snug focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
              <button
                type="button"
                disabled={!text.trim() || add.isPending || techBusy}
                onClick={() => void submitAdd()}
                className="h-[42px] rounded-lg bg-brand-700 px-4 text-sm font-semibold text-white shadow-e1 hover:bg-brand-800 disabled:opacity-50"
              >
                {add.isPending || techBusy ? "Adding…" : "Add"}
              </button>
            </div>
            {locno && (
              <label className="mt-2 flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={thisPropertyOnly} onChange={(e) => setThisPropertyOnly(e.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500/40" />
                Only this property (unchecked = every property on the account)
              </label>
            )}
          </div>
        )}
        {dnsOpen && (
          <div className="mt-2.5 rounded-lg border border-rose-200 bg-white p-3">
            <p className="text-xs text-slate-600">A red badge goes on the account and nobody can book new dispatches onto it. History stays viewable; reversible under Edit ▾ on the name.</p>
            <div className="mt-2 flex items-start gap-2">
              <input
                type="text"
                autoFocus
                value={dnsReason}
                onChange={(e) => setDnsReason(e.target.value)}
                placeholder="Reason — e.g. unpaid balance since 2024, abusive to techs"
                className="min-w-0 flex-1 rounded-lg border border-hairline px-3 py-2 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
              />
              <button
                type="button"
                disabled={setDns.isPending}
                onClick={() => setDns.mutate({ custno, on: true, reason: dnsReason.trim() || null }, { onSuccess: () => { setDnsOpen(false); setDnsReason(""); }, onError: onErrToast("Couldn't mark Do Not Service") })}
                className="rounded-lg bg-rose-700 px-3.5 py-2 text-sm font-semibold text-white hover:bg-rose-800 disabled:opacity-50"
              >
                Mark Do Not Service
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ---------------------------------------------------------------- found */}
      <section className="mt-5">
        <Eyebrow count={w.found.length} hint={w.found.length > 0 ? "nothing here shows to the team until you keep it" : undefined}>Pegasus found these in old notes</Eyebrow>
        {w.found.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">Nothing waiting — Pegasus re-reads the notes as new ones land.</p>
        ) : (
          <ul className="mt-1 divide-y divide-slate-100">
            {w.found.map((s) => (
              <StoredRow
                key={s.id}
                s={s}
                locno={locno}
                actions={
                  <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
                    <button type="button" onClick={() => resolve.mutate({ id: s.id, status: "confirmed" }, { onError: onErrToast("Couldn't keep it") })} className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100">
                      <Check className="h-3.5 w-3.5" /> Keep
                    </button>
                    <button type="button" onClick={() => resolve.mutate({ id: s.id, status: "dismissed" }, { onError: onErrToast("Couldn't reject it") })} className="inline-flex h-7 items-center gap-1 rounded-md bg-white px-2.5 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50">
                      <X className="h-3.5 w-3.5" /> Not right
                    </button>
                  </div>
                }
              />
            ))}
          </ul>
        )}
        {w.found.length > 0 && (
          <p className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400">
            <Sparkles className="h-3 w-3" /> Pulled from years of notes — some were one-day instructions. Keep only what&apos;s always true.
          </p>
        )}
      </section>

      {/* ---------------------------------------------------------------- dismissed */}
      {w.dismissed.length > 0 && (
        <section className="mt-5 border-t border-slate-100 pt-3">
          <button type="button" onClick={() => setShowDismissed((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
            {showDismissed ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {w.dismissed.length} rejected or removed
          </button>
          {showDismissed && (
            <ul className="divide-y divide-slate-100 opacity-70">
              {w.dismissed.map((s) => (
                <StoredRow
                  key={s.id}
                  s={s}
                  locno={locno}
                  actions={
                    <button type="button" onClick={() => resolve.mutate({ id: s.id, status: "confirmed" }, { onError: onErrToast("Couldn't bring it back") })} title="Bring it back onto the record" className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md bg-white px-2.5 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-200 hover:bg-brand-50">
                      <RotateCcw className="h-3.5 w-3.5" /> Bring back
                    </button>
                  }
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

/** Remove asks twice (Seth 2026-09-01: "make it a little harder to click"). */
function RemoveButton({ onRemove }: { onRemove: () => void }) {
  const [armed, setArmed] = useState(false);
  return armed ? (
    <button type="button" onClick={() => { onRemove(); setArmed(false); }} onBlur={() => setArmed(false)} autoFocus className="rounded-md bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm hover:bg-rose-700">
      Remove?
    </button>
  ) : (
    <button type="button" onClick={() => { setArmed(true); window.setTimeout(() => setArmed(false), 3500); }} title="Remove — asks once more; recoverable under “rejected or removed”" className="text-[11px] font-medium text-slate-300 hover:text-rose-600">
      Remove
    </button>
  );
}
